import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { paths } from '@fleet/environment';
import {
  ApiResponse,
  LoginModel,
  MultiFactorAuthenticationModel,
  VerificationModel,
} from '@fleet/model';
import { NetworkGroupHeaderService } from '@fleet/token';
import { handleApiError } from '@fleet/utilities';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class VerificationApiService {
  private nonAuthorisedHttp: HttpClient;
  host: string;
  fleetProduct: string;

  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    @Inject('env') env: any,
    private networkGroupHeaderService: NetworkGroupHeaderService
  ) {
    this.nonAuthorisedHttp = new HttpClient(backend);
    this.host = env.host + paths.verify;
    this.fleetProduct = env.fleetProduct;
  }

  private getNonAuthorizedHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'fleet-channel': 'WEB',
      'fleet-product': this.fleetProduct,
    });

    const networkGroupId = this.networkGroupHeaderService.networkGroupId();
    if (networkGroupId) {
      headers = headers.set('fleet-network-group-id', networkGroupId);
    }

    return headers;
  }

  getInvitation(activationKey: string) {
    return this.nonAuthorisedHttp
      .get(`${this.host}/invitation/${activationKey}`, {
        headers: this.getNonAuthorizedHeaders(),
      })
      .pipe(catchError(handleApiError));
  }

  sendEmailActivationCode(payload: any) {
    return this.nonAuthorisedHttp
      .post(`${this.host}/email`, payload, {
        headers: this.getNonAuthorizedHeaders(),
      })
      .pipe(catchError(handleApiError));
  }

  verifyEmail(
    verificationModel: VerificationModel
  ): Observable<ApiResponse<VerificationModel> | any> {
    return this.nonAuthorisedHttp
      .post(`${this.host}/email`, verificationModel, {
        headers: this.getNonAuthorizedHeaders(),
      })
      .pipe(catchError(handleApiError));
  }

  verifyEmailWithJWT(
    verificationModel: VerificationModel
  ): Observable<ApiResponse<VerificationModel> | any> {
    const jwt = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + jwt,
      'fleet-channel': 'WEB',
      'fleet-product': this.fleetProduct,
    });
    return this.http
      .post(`${this.host}/email`, verificationModel, { headers: headers })
      .pipe(catchError(handleApiError));
  }

  verify(verificationModel: VerificationModel): Observable<any> {
    return this.nonAuthorisedHttp
      .post(`${this.host}`, verificationModel, {
        headers: this.getNonAuthorizedHeaders(),
      })
      .pipe(catchError(handleApiError));
  }

  verifyFullResponse(verificationModel: VerificationModel): Observable<any> {
    return this.nonAuthorisedHttp.post(`${this.host}`, verificationModel, {
      headers: this.getNonAuthorizedHeaders(),
    });
  }

  verifyWithJWT(verificationModel: VerificationModel): Observable<any> {
    const jwt = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + jwt,
      'fleet-channel': 'WEB',
      'fleet-product': this.fleetProduct,
    });
    return this.http.post(`${this.host}`, verificationModel, {
      headers: headers,
    });
  }

  verifyPhoneNumber(
    payload: any,
    includeJwt: boolean
  ): Observable<ApiResponse<VerificationModel> | any> {
    if (includeJwt) {
      const jwt = localStorage.getItem('access_token');
      const headers = new HttpHeaders({
        Authorization: 'Bearer ' + jwt,
        'fleet-channel': 'WEB',
        'fleet-product': this.fleetProduct,
      });
      return this.http
        .post(`${this.host}/phoneNumber`, payload, { headers })
        .pipe(catchError(handleApiError));
    }

    return this.nonAuthorisedHttp
      .post(`${this.host}/phoneNumber`, payload, {
        headers: this.getNonAuthorizedHeaders(),
      })
      .pipe(catchError(handleApiError));
  }

  verifyCreditCard(
    payload: any
  ): Observable<ApiResponse<VerificationModel> | any> {
    return this.http
      .post(`${this.host}/creditCard`, payload)
      .pipe(catchError(handleApiError));
  }

  verifyGoogleSignin(
    payload: VerificationModel
  ): Observable<ApiResponse<VerificationModel> | any> {
    return this.http
      .post(`${this.host}/google/signin`, payload, {
        observe: 'response',
      })
      .pipe(catchError(handleApiError));
  }

  verifyApplSignin(
    payload: VerificationModel
  ): Observable<ApiResponse<VerificationModel> | any> {
    return this.http
      .post(`${this.host}/apple/signin`, payload)
      .pipe(catchError(handleApiError));
  }

  verifyMfa(
    mfa: MultiFactorAuthenticationModel,
    login: LoginModel
  ): Observable<ApiResponse<VerificationModel> | any> {
    if (login) {
      // User is not fully logged in
      const authdata = btoa(login.username + ':' + login.password);
      let headers = new HttpHeaders({
        Authorization: 'Basic ' + authdata,
        'fleet-product': this.fleetProduct,
        'fleet-channel': 'WEB',
      });

      const networkGroupId = this.networkGroupHeaderService.networkGroupId();
      if (networkGroupId) {
        headers = headers.set('fleet-network-group-id', networkGroupId);
      }

      return this.nonAuthorisedHttp
        .post(`${this.host}/MFA`, mfa, { headers })
        .pipe(catchError(handleApiError));
    }
    return this.http
      .post(`${this.host}/MFA`, mfa)
      .pipe(catchError(handleApiError));
  }

  sendMFACode(
    mfa: MultiFactorAuthenticationModel,
    login: LoginModel
  ): Observable<any> {
    return this.verifyMfa(mfa, login);
  }
}
